/**
 * Tool registry and adapter surface.
 * Exposes harmonized tool adapters with unified interface.
 */

import { loadAllToolSpecs } from "./specLoader";
import type { NormalizedToolSpec } from "./specLoader";
import { mapNativeToCanonical } from "./outputMapping";
import type {
  HarmonizedToolAdapter,
  Item,
  CriteriaSet,
  ToolConfig,
  Lens,
  CanonicalScoreSet,
} from "./types";

let adapters: HarmonizedToolAdapter[] | null = null;

/**
 * Initializes the registry by loading all TOOL_SPEC files.
 * Should be called once at application startup.
 * 
 * @param rootDir - Root directory to search for TOOL_SPEC files (defaults to current directory)
 */
export function initializeRegistry(rootDir?: string): void {
  if (adapters !== null) {
    console.warn("Registry already initialized. Skipping re-initialization.");
    return;
  }

  adapters = [];
  
  // Try to load specs from files if in Node.js environment
  if (typeof process !== 'undefined' && process.cwd) {
    try {
      const searchRoot = rootDir || process.cwd();
      const specs = loadAllToolSpecs(searchRoot);
      adapters = specs.map((spec) => createAdapter(spec));
    } catch (e) {
      // In browser environment, loadAllToolSpecs may fail
      console.log("Could not load tool specs from filesystem (browser mode)");
    }
  }
}

/**
 * Manually registers tool specs (useful for browser mode).
 * 
 * @param specs - Array of normalized tool specs to register
 */
export function registerToolSpecs(specs: NormalizedToolSpec[]): void {
  if (adapters === null) {
    adapters = [];
  }
  
  const newAdapters = specs.map((spec) => createAdapter(spec));
  adapters.push(...newAdapters);
}

/**
 * Creates a harmonized adapter from a normalized spec.
 * The runTool function is a placeholder that must be wired with actual implementations.
 */
function createAdapter(spec: NormalizedToolSpec): HarmonizedToolAdapter {
  return {
    toolName: spec.toolName,
    category: spec.category,
    flags: spec.flags,
    inputContract: spec.inputContract,
    interactionModel: spec.interactionModel,
    runTool: async (
      items: Item[],
      criteriaSet: CriteriaSet | null,
      toolConfig: ToolConfig,
      lens?: Lens | null
    ): Promise<CanonicalScoreSet> => {
      throw new Error(
        `runTool not implemented for tool '${spec.toolName}'. Wire concrete implementation in Arrange engine.`
      );
    },
  };
}

/**
 * Gets all harmonized tool adapters.
 * 
 * @returns Array of all adapters
 * @throws Error if registry not initialized
 */
export function getAllAdapters(): HarmonizedToolAdapter[] {
  if (adapters === null) {
    throw new Error(
      "Registry not initialized. Call initializeRegistry() first."
    );
  }
  return [...adapters];
}

/**
 * Gets an adapter by tool name.
 * 
 * @param toolName - Name of the tool to find
 * @returns Adapter if found, undefined otherwise
 * @throws Error if registry not initialized
 */
export function getAdapterByName(
  toolName: string
): HarmonizedToolAdapter | undefined {
  if (adapters === null) {
    throw new Error(
      "Registry not initialized. Call initializeRegistry() first."
    );
  }
  return adapters.find((a) => a.toolName === toolName);
}

/**
 * Gets adapters by category.
 * 
 * @param category - Tool category to filter by
 * @returns Array of adapters in the specified category
 * @throws Error if registry not initialized
 */
export function getAdaptersByCategory(
  category: HarmonizedToolAdapter["category"]
): HarmonizedToolAdapter[] {
  if (adapters === null) {
    throw new Error(
      "Registry not initialized. Call initializeRegistry() first."
    );
  }
  return adapters.filter((a) => a.category === category);
}

/**
 * Wires a concrete tool implementation to an adapter.
 * This replaces the placeholder runTool function with an actual implementation.
 * 
 * @param toolName - Name of the tool to wire
 * @param runTool - Concrete implementation function
 */
export function wireToolImplementation(
  toolName: string,
  runTool: HarmonizedToolAdapter["runTool"]
): void {
  if (adapters === null) {
    throw new Error(
      "Registry not initialized. Call initializeRegistry() first."
    );
  }

  const adapter = adapters.find((a) => a.toolName === toolName);
  if (!adapter) {
    throw new Error(`Tool '${toolName}' not found in registry.`);
  }

  // Replace the runTool function
  adapter.runTool = runTool;
}

/**
 * Helper function to create a runTool implementation that wraps a native tool
 * and automatically maps its output to canonical format.
 * 
 * @param spec - Normalized tool specification
 * @param nativeRunner - Function that returns native score set
 * @returns Canonical runTool implementation
 */
export function createCanonicalRunner(
  spec: NormalizedToolSpec,
  nativeRunner: (
    items: Item[],
    criteriaSet: CriteriaSet | null,
    toolConfig: ToolConfig,
    lens?: Lens | null
  ) => Promise<{ items: any[]; toolRunId: string; metadata?: Record<string, any> }>
): HarmonizedToolAdapter["runTool"] {
  return async (
    items: Item[],
    criteriaSet: CriteriaSet | null,
    toolConfig: ToolConfig,
    lens?: Lens | null
  ): Promise<CanonicalScoreSet> => {
    const native = await nativeRunner(items, criteriaSet, toolConfig, lens);
    return mapNativeToCanonical(spec, native);
  };
}


